import {
  SkyBadge,
  SkyButton,
  SkyCard,
  SkyDropdown,
  SkyInput,
  SkyMessageBar,
  SkyText,
} from "@skypointcloud/skypoint-ui";
import { useLayoutEffect, useRef, useState, type ComponentPropsWithoutRef } from "react";
import { TopbarActionsPortal } from "@/components/navigation/TopbarActionsPortal";
import { ReauthDialog } from "@/components/settings/ReauthDialog";
import { ReplaceSecretDialog } from "@/components/settings/ReplaceSecretDialog";
import { SecretReferenceField } from "@/components/settings/SecretReferenceField";
import type { BindingSaveState } from "@/hooks/use-binding";
import { secretFieldIssues, useDbtProbe } from "@/hooks/use-binding";
import { ApiClientError } from "@/lib/api";
import { FORM_MAX_WIDTH } from "@/lib/layout";
import {
  dbtProjectSubdirRefusal,
  dbtRefRefusal,
  dbtRepoUrlRefusal,
  type InstanceBindingView,
} from "@shared/types/instances";
import {
  DBT_NEW_REPO_TOKEN_MESSAGE,
  isBindingSecretCopy,
  normaliseDbtRepoUrlForCompare,
  stripDbtRepoUrlUsername,
  type BindingSecretCopy,
  type DbtProbeBody,
  type DbtProbeReason,
  type DbtProbeResult,
  type PlatformUpdateBindingBody,
  type UpdateDbtSourceBody,
} from "@shared/types/instancesAdmin";

/**
 * The Transformation source tab of the destination page (Session 10 P5, B14-B16).
 *
 * The repository the transform step clones, its ref and its project folder, the access token,
 * and **Connect**, which reads what the repository offers without saving anything: its
 * branches, its tags and the folders holding a `dbt_project.yml`. Before Connect the ref and the
 * folder are free text; after it they are lists, each with a way back to typing.
 *
 * Two rules come from the server and are enforced here first so the user is told why:
 *
 * - **A new repository needs its own token (B5).** When the address differs from the saved one
 *   (compared the way the server compares), Connect and Save both wait for a typed token. The
 *   saved token was installed for the saved repository and is never spent on another.
 * - **An Azure DevOps address pasted with `<org>@` (SF5)** is cleaned when the field is left,
 *   with a note, because the `<org>@` is not a credential but the shared rule refuses any user
 *   name in an address. A `user:password@` address is never cleaned: it is refused.
 *
 * The draft is the page's, so it survives a switch to the Warehouse tab and a trip through the
 * identity provider; the save is the page's `useSaveBinding` pointed at the dbt route, so the
 * re-authentication behaviour is the one the Warehouse save has.
 *
 * Editing needs `dbt_jobs.write`. Everyone else who can read the binding sees the same
 * tab read-only: fields `readOnly` (not disabled, so they stay readable and in the tab order),
 * no Connect, no Save.
 *
 * B28 (copy mode): with `allowCopy` (the page passes `instance.binding.write`, the platform
 * route's permission) the token's Replace dialog can name a secret already in the deployment's
 * vault instead of taking a value. A body holding a copy is saved through the platform route,
 * the only one that accepts it (`useSaveBinding` picks the route from the body), and a copy
 * satisfies the new-repository rule as a typed token does.
 */

export interface DbtDraft {
  repoUrl: string;
  branch: string;
  subdir: string;
  /**
   * A typed token held for the save, `null` to clear, a copy of an existing vault secret (B28,
   * platform admins only), or absent to leave the saved one.
   */
  token?: string | null | BindingSecretCopy;
}

/** The dbt save body: the dbt route's, or the platform route's when the token is a copy. */
export type DbtSourceSaveBody = UpdateDbtSourceBody | PlatformUpdateBindingBody;

export type DbtField = "repoUrl" | "branch" | "subdir";

export const DBT_FIELD_LABELS: Record<DbtField, string> = {
  repoUrl: "dbt repository URL",
  branch: "dbt branch",
  subdir: "dbt project subdirectory",
};

const TEST_IDS: Record<DbtField, string> = {
  repoUrl: "binding-dbtRepoUrl",
  branch: "binding-dbtBranch",
  subdir: "binding-dbtProjectSubdir",
};

/** What Connect's reasons mean, in the reader's words. */
export const PROBE_REASON_SENTENCES: Record<DbtProbeReason, string> = {
  auth: "The token was refused. Check that it can read this repository.",
  not_found: "No repository was found at this address, or the token cannot see it.",
  redirect:
    "The host answered with a redirect, which Connect does not follow. Check the address. A renamed repository keeps redirecting.",
  timeout: "The repository did not answer within 10 seconds. Try again.",
  network: "Connect could not reach the repository host.",
  not_git: "This address did not answer as a git repository.",
};

const USE_TEXT_REF = "__use_text_ref__";
const USE_TEXT_FOLDER = "__use_text_folder__";
/** The dropdown's value for the repository root, whose folder is the empty string. */
const ROOT_FOLDER = "__root__";

export function dbtDraftFromBinding(binding: InstanceBindingView): DbtDraft {
  return {
    repoUrl: binding.dbtRepoUrl ?? "",
    branch: binding.dbtBranch ?? "",
    subdir: binding.dbtProjectSubdir ?? "",
  };
}

/** The address as it will be sent: trimmed, with a username-only userinfo removed. */
function cleanUrl(value: string): string {
  return stripDbtRepoUrlUsername(value.trim()).url;
}

function isAzureDevOpsHost(url: string): boolean {
  try {
    const host = new URL(url).hostname.toLowerCase();
    return host === "dev.azure.com" || host.endsWith(".visualstudio.com");
  } catch {
    return false;
  }
}

/** The note shown when a user name was removed; names an Azure DevOps organisation only. */
function removedNote(removed: string, cleaned: string): string {
  return isAzureDevOpsHost(cleaned)
    ? `Removed '${removed}@' from the address. The token goes in Access token.`
    : "Removed the user name from the address. The token goes in Access token.";
}

/** The address as the server compares it; `null` when empty. */
function addressKey(value: string | null | undefined): string | null {
  return normaliseDbtRepoUrlForCompare(value ?? "");
}

/**
 * A `SkyMessageBar` that does not announce itself. The library hard-codes `role` and
 * `aria-live` on every bar, so a bar inside a live region is announced twice; the region is
 * the one that announces (one announcement per message, the Session 10 round-3 decision, the
 * way P13a's notice bars do it). Runs after every render, so a re-render cannot restore them.
 */
function QuietMessageBar(props: ComponentPropsWithoutRef<typeof SkyMessageBar>) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    ref.current?.removeAttribute("role");
    ref.current?.removeAttribute("aria-live");
  });
  return <SkyMessageBar {...props} ref={ref} />;
}

/** True when the draft names a different repository from the saved one (B5). */
export function repoChanged(draft: DbtDraft, binding: InstanceBindingView): boolean {
  const next = cleanUrl(draft.repoUrl);
  if (next === "") return false;
  return normaliseDbtRepoUrlForCompare(next) !== normaliseDbtRepoUrlForCompare(binding.dbtRepoUrl);
}

/** A typed token is a string; a staged clear (null) or nothing at all is not one. */
function hasTypedToken(draft: DbtDraft): boolean {
  return typeof draft.token === "string" && draft.token.length > 0;
}

/** A new token for the save: typed, or copied from an existing vault secret (B28). */
function hasNewToken(draft: DbtDraft): boolean {
  return hasTypedToken(draft) || isBindingSecretCopy(draft.token);
}

/** Why a CHANGED field would be refused by the server, by field (the shared rules). */
export function dbtSourceRefusals(
  draft: DbtDraft,
  binding: InstanceBindingView,
): Partial<Record<DbtField, string>> {
  const refusals: Partial<Record<DbtField, string>> = {};
  const url = cleanUrl(draft.repoUrl);
  if (url !== (binding.dbtRepoUrl ?? "") && url !== "") {
    const refusal = dbtRepoUrlRefusal(url);
    if (refusal) refusals.repoUrl = refusal;
  }
  const branch = draft.branch.trim();
  if (branch !== (binding.dbtBranch ?? "") && branch !== "") {
    const refusal = dbtRefRefusal(branch);
    if (refusal) refusals.branch = refusal;
  }
  const subdir = draft.subdir.trim();
  if (subdir !== (binding.dbtProjectSubdir ?? "") && subdir !== "") {
    const refusal = dbtProjectSubdirRefusal(subdir);
    if (refusal) refusals.subdir = refusal;
  }
  return refusals;
}

/** The save body: a diff against the saved binding, so an untouched field is never sent. */
export function buildDbtBody(
  draft: DbtDraft,
  binding: InstanceBindingView,
): DbtSourceSaveBody | null {
  const body: Record<string, unknown> = {};
  const url = cleanUrl(draft.repoUrl);
  if (url !== (binding.dbtRepoUrl ?? "")) body.dbtRepoUrl = url === "" ? null : url;
  const branch = draft.branch.trim();
  if (branch !== (binding.dbtBranch ?? "")) body.dbtBranch = branch === "" ? null : branch;
  const subdir = draft.subdir.trim();
  if (subdir !== (binding.dbtProjectSubdir ?? "")) {
    body.dbtProjectSubdir = subdir === "" ? null : subdir;
  }
  if (draft.token !== undefined) body.secrets = { dbtGitSecret: draft.token };
  return Object.keys(body).length > 0 ? (body as DbtSourceSaveBody) : null;
}

/** What the save will do, for the re-authentication dialog. Never the token. */
function summarise(draft: DbtDraft, binding: InstanceBindingView): string[] {
  const lines: string[] = [];
  const pairs: Array<[DbtField, string, string]> = [
    ["repoUrl", binding.dbtRepoUrl ?? "", cleanUrl(draft.repoUrl)],
    ["branch", binding.dbtBranch ?? "", draft.branch.trim()],
    ["subdir", binding.dbtProjectSubdir ?? "", draft.subdir.trim()],
  ];
  for (const [field, before, after] of pairs) {
    if (before !== after) {
      lines.push(`${DBT_FIELD_LABELS[field]}: ${before || "empty"} → ${after || "empty"}`);
    }
  }
  if (draft.token === null) lines.push("Access token: cleared");
  else if (typeof draft.token === "string") lines.push("Access token: new value");
  else if (isBindingSecretCopy(draft.token)) {
    lines.push(`Access token: copy of ${draft.token.copyFrom.name}`);
  }
  return lines;
}

/** The sentence for a refused Connect or save: the field's own issue when the server named one. */
function errorSentence(error: unknown, fallback: string): string {
  if (error instanceof ApiClientError) {
    const detail = error.details.find((item) => item.issue)?.issue;
    if (detail) return detail;
    if (error.code === "RATE_LIMITED")
      return "Too many Connect attempts. Wait a minute and try again.";
    return error.message || fallback;
  }
  return fallback;
}

/** The probe request: the address, and the branch, folder and typed token when they are usable. */
function probeRequestOf(
  draft: DbtDraft,
  refusals: Partial<Record<DbtField, string>>,
  address: string,
): DbtProbeBody {
  const request: DbtProbeBody = { dbtRepoUrl: address };
  if (draft.branch.trim() && !refusals.branch) request.dbtBranch = draft.branch.trim();
  if (draft.subdir.trim() && !refusals.subdir) request.dbtProjectSubdir = draft.subdir.trim();
  if (hasTypedToken(draft)) request.dbtGitSecret = draft.token as string;
  return request;
}

/**
 * Connect fills the lists and picks what it can: the default branch when the ref is empty (the
 * runner's own default is `main`, which may not be this repository's), and the one project
 * folder when there is exactly one. A tag or a sha stays as text: the list holds branches.
 */
function connectPicks(
  latest: DbtDraft,
  answer: DbtProbeResult,
): { patch: Partial<DbtDraft>; branchAsText: boolean; folderAsText: boolean } {
  const patch: Partial<DbtDraft> = {};
  if (latest.branch.trim() === "" && answer.defaultBranch) patch.branch = answer.defaultBranch;
  const nextBranch = patch.branch ?? latest.branch.trim();
  const dirs = answer.projectDirs;
  if (dirs && dirs.length === 1 && !dirs.includes(latest.subdir.trim())) {
    patch.subdir = dirs[0]!;
  }
  const nextSubdir = patch.subdir ?? latest.subdir.trim();
  return {
    patch,
    branchAsText: nextBranch !== "" && !answer.branches.includes(nextBranch),
    folderAsText: dirs === null || (nextSubdir !== "" && !dirs.includes(nextSubdir)),
  };
}

function connectReady({
  url,
  refusals,
  needsToken,
  copyAwaitsSave,
  pending,
}: {
  url: string;
  refusals: Partial<Record<DbtField, string>>;
  needsToken: boolean;
  copyAwaitsSave: boolean;
  pending: boolean;
}): boolean {
  return url !== "" && !refusals.repoUrl && !needsToken && !copyAwaitsSave && !pending;
}

function branchOptionsOf(result: DbtProbeResult) {
  return [
    ...result.branches.map((name) => ({
      value: name,
      text: name === result.defaultBranch ? `${name} (default)` : name,
    })),
    { value: USE_TEXT_REF, text: "Use a tag or commit instead" },
  ];
}

function folderOptionsOf(projectDirs: string[]) {
  return [
    ...projectDirs.map((dir) => ({
      value: dir === "" ? ROOT_FOLDER : dir,
      text: dir === "" ? "(repository root)" : dir,
    })),
    { value: USE_TEXT_FOLDER, text: "Type a folder" },
  ];
}

/**
 * What Connect's answer offers: the branches, tags and project folders it listed, and whether
 * the branch and folder fields show as lists (only for an editor who has not chosen to type).
 */
function connectChoices(
  result: DbtProbeResult | null,
  form: { branchAsText: boolean; folderAsText: boolean; canEdit: boolean },
) {
  const listed = result?.ok === true;
  const branchOptions = listed ? branchOptionsOf(result) : [];
  const tags = listed ? result.tags : [];
  const folderOptions = listed && result.projectDirs ? folderOptionsOf(result.projectDirs) : [];
  const showBranchList = listed && !form.branchAsText && form.canEdit;
  const showFolderList = listed && folderOptions.length > 1 && !form.folderAsText && form.canEdit;
  return { branchOptions, tags, folderOptions, showBranchList, showFolderList };
}

/** The branch field's hint, naming tags Connect found. */
function branchHint(tags: string[]): string {
  return tags.length > 0
    ? `A branch, a tag (such as ${tags.slice(0, 3).join(", ")}) or a 40-character commit sha. Empty means main.`
    : "A branch, a tag or a 40-character commit sha. Empty means main.";
}

/** What this tab is for an editor, or that a reader cannot change it. */
function DbtIntro({ canEdit }: { canEdit: boolean }) {
  if (canEdit) {
    return (
      <SkyMessageBar
        intent="info"
        layout="multiline"
        title="The repository your dbt jobs run from"
        message="Connect reads the repository without saving anything. A saved change applies from the next run."
      />
    );
  }
  return (
    <SkyMessageBar
      intent="warning"
      layout="multiline"
      testId="dbt-read-only"
      title="You can read this dbt repository but not change it"
      message="An instance admin can change it."
    />
  );
}

/** The access token, by reference only, and the note that a new repository needs its own. */
function TokenField({
  binding,
  staged,
  readOnly,
  error,
  needsToken,
  onReplace,
  onClear,
  onDiscardStaged,
}: {
  binding: InstanceBindingView;
  staged: DbtDraft["token"];
  readOnly: boolean;
  error: string | undefined;
  needsToken: boolean;
  onReplace: () => void;
  onClear: () => void;
  onDiscardStaged: () => void;
}) {
  return (
    <div className="space-y-2">
      <SecretReferenceField
        label="Access token"
        hint="A fine-grained token with read access to this one repository. skyData stores it securely and never shows it again."
        pointer={binding.dbtGitSecret}
        stored={binding.secretsStored?.dbtGitSecret}
        staged={staged}
        readOnly={readOnly}
        readOnlyNote="Instance admin and above"
        error={error}
        testId="secret-dbtGitSecret"
        onReplace={readOnly ? undefined : onReplace}
        onClear={readOnly ? undefined : onClear}
        onDiscardStaged={readOnly ? undefined : onDiscardStaged}
      />
      <div aria-live="polite" data-testid="dbt-token-required-region">
        {needsToken ? (
          <QuietMessageBar
            intent="warning"
            layout="multiline"
            testId="dbt-token-required"
            message={DBT_NEW_REPO_TOKEN_MESSAGE}
          />
        ) : null}
      </div>
    </div>
  );
}

/** Connect, and what it does (or that a copied token waits for the save). */
function ConnectRow({
  canConnect,
  pending,
  copyAwaitsSave,
  onConnect,
}: {
  canConnect: boolean;
  pending: boolean;
  copyAwaitsSave: boolean;
  onConnect: () => void;
}) {
  return (
    <div className="mt-4 flex flex-wrap items-center gap-3">
      <SkyButton
        appearance="secondary"
        testId="dbt-connect"
        disabled={!canConnect}
        loading={pending}
        onClick={onConnect}
      >
        Connect
      </SkyButton>
      <SkyText as="p" size={200} color="muted" data-testid="dbt-connect-note">
        {copyAwaitsSave
          ? "Save to use the copied token. Connect then reads this repository with it."
          : "Lists the branches and finds dbt_project.yml. Nothing is saved."}
      </SkyText>
    </div>
  );
}

/** "3 branches and 2 tags. Read at main (abc1234), with the saved token." */
function connectedSentence(result: DbtProbeResult): string {
  const branches = `${result.branches.length} ${result.branches.length === 1 ? "branch" : "branches"}`;
  const tags = `${result.tags.length} ${result.tags.length === 1 ? "tag" : "tags"}`;
  const commit = result.commit ? ` (${result.commit.slice(0, 7)})` : "";
  const token = result.usedStoredSecret ? ", with the saved token" : "";
  return `${branches} and ${tags}. Read at ${result.ref}${commit}${token}.`;
}

/** What Connect found: its failure, the branches and tags, the project's notes and warnings. */
function ConnectResults({
  result,
  connectError,
}: {
  result: DbtProbeResult | null;
  connectError: string | null;
}) {
  return (
    <div className="mt-4 space-y-2" aria-live="polite" data-testid="dbt-connect-results">
      {connectError ? (
        <QuietMessageBar
          intent="error"
          layout="multiline"
          testId="dbt-connect-error"
          title="Connect did not complete"
          message={connectError}
        />
      ) : null}
      {result && !result.ok && result.reason ? (
        <QuietMessageBar
          intent="error"
          layout="multiline"
          testId="dbt-connect-result"
          title="Connect failed"
          message={PROBE_REASON_SENTENCES[result.reason]}
        />
      ) : null}
      {result?.ok ? (
        <QuietMessageBar
          intent="success"
          layout="multiline"
          testId="dbt-connect-result"
          title="Connected"
          message={connectedSentence(result)}
        />
      ) : null}
      {result?.ok && result.project?.profileNote ? (
        <QuietMessageBar
          intent="info"
          layout="multiline"
          testId="dbt-profile-note"
          message={result.project.profileNote}
        />
      ) : null}
      {result?.ok && result.project?.versionOk === true ? (
        <SkyText as="p" size={200} data-testid="dbt-version-check">
          {`require-dbt-version ${result.project.requireDbtVersion ?? ""} is met.`}
        </SkyText>
      ) : null}
      {result && result.warnings.length > 0 ? (
        <QuietMessageBar
          intent="warning"
          layout="multiline"
          testId="dbt-connect-warnings"
          message={
            <ul className="list-disc pl-5">
              {result.warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          }
        />
      ) : null}
    </div>
  );
}

/** Save and Discard, the refusals and the server's error, the token dialog and the re-auth hold. */
function DbtSaveControls({
  saver,
  body,
  saveable,
  refusals,
  refused,
  changeSummary,
  replacing,
  onReplacingChange,
  onStageToken,
  allowCopy,
  onDiscard,
}: {
  saver: BindingSaveState<DbtSourceSaveBody>;
  body: DbtSourceSaveBody | null;
  saveable: boolean;
  refusals: Partial<Record<DbtField, string>>;
  refused: boolean;
  changeSummary: string[];
  replacing: boolean;
  onReplacingChange: (open: boolean) => void;
  onStageToken: (token: string | BindingSecretCopy) => void;
  allowCopy: boolean;
  onDiscard: () => void;
}) {
  return (
    <>
      <TopbarActionsPortal>
        <SkyButton
          appearance="primary"
          testId="save-dbt-source-topbar"
          disabled={!saveable}
          loading={saver.isSaving}
          onClick={() => saveable && body && void saver.save(body)}
        >
          Save changes
        </SkyButton>
      </TopbarActionsPortal>

      {refused ? (
        <SkyMessageBar
          intent="error"
          layout="multiline"
          testId="dbt-source-refusals"
          title="Fix the dbt fields before saving"
          message={(Object.entries(refusals) as [DbtField, string][])
            .map(([field, refusal]) => `${DBT_FIELD_LABELS[field]}: ${refusal}`)
            .join(" ")}
        />
      ) : null}
      {saver.error ? (
        <SkyMessageBar
          intent="error"
          layout="multiline"
          testId="dbt-save-error"
          title="The dbt repository was not saved"
          message={errorSentence(saver.error, "Try again.")}
        />
      ) : null}

      {/* #1674: the save stands, and the server says what is worth a second look beside Save. */}
      {saver.warnings.map((warning) => (
        <SkyMessageBar
          key={warning.code}
          intent="warning"
          layout="multiline"
          testId="dbt-save-warning"
          title="Saved. Check the dbt branch"
          message={warning.message}
        />
      ))}

      <div className="flex flex-wrap items-center gap-3 border-t border-border pt-3">
        <SkyText as="p" size={200} color="muted">
          This change affects the next run only. A transform already running keeps the repository it
          started with.
        </SkyText>
        <span className="ml-auto flex items-center gap-2">
          <SkyButton
            appearance="subtle"
            testId="discard-dbt-source"
            disabled={!body}
            onClick={onDiscard}
          >
            Discard
          </SkyButton>
          <SkyButton
            appearance="primary"
            testId="save-dbt-source"
            disabled={!saveable}
            loading={saver.isSaving}
            onClick={() => saveable && body && void saver.save(body)}
          >
            Save changes
          </SkyButton>
        </span>
      </div>

      <ReplaceSecretDialog
        open={replacing}
        title="Replace the access token"
        // A token only: ADR 0004 rules out SSH and deploy keys (the image clones over
        // https with the token as the password).
        label="Personal access token"
        multiline={false}
        onOpenChange={(open) => {
          if (!open) onReplacingChange(false);
        }}
        onStage={(value) => onStageToken(value)}
        onStageCopy={allowCopy ? (name) => onStageToken({ copyFrom: { name } }) : undefined}
      />

      <ReauthDialog
        open={saver.isReauthOpen}
        isRetrying={saver.isSaving}
        changeSummary={
          <ul className="list-disc pl-5">
            {changeSummary.map((line) => (
              <li key={line}>
                <SkyText size={200}>{line}</SkyText>
              </li>
            ))}
          </ul>
        }
        error={saver.reauthError}
        code={saver.reauthCode}
        blocked={saver.reauthBlocked}
        remember={saver.reauthRemember}
        onConfirm={() => void saver.retryAfterReauth()}
        onCancel={saver.cancelReauth}
      />
    </>
  );
}

/** What the form can do with this draft: the token rule (B5), the save, and Connect. */
function formState({
  draft,
  binding,
  canEdit,
  refusals,
  changedRepo,
  saveError,
  probePending,
}: {
  draft: DbtDraft;
  binding: InstanceBindingView;
  canEdit: boolean;
  refusals: Partial<Record<DbtField, string>>;
  changedRepo: boolean;
  saveError: unknown;
  probePending: boolean;
}) {
  const needsToken = canEdit && changedRepo && !hasNewToken(draft);
  // B28: the server's sentence for a refused copy source, at the token field while it is staged.
  const tokenError =
    draft.token !== undefined ? secretFieldIssues(saveError).dbtGitSecret : undefined;
  const body = buildDbtBody(draft, binding);
  const refused = Object.keys(refusals).length > 0;
  const saveBlocked = refused || needsToken;
  const saveable = body !== null && !saveBlocked;
  const url = cleanUrl(draft.repoUrl);
  // B28 review r1: Connect has no copy mode, so with a new address and a staged COPY it would
  // send no token and answer its own "enter the token" refusal. It waits for the save instead;
  // once saved, the copy is the stored token for this address and Connect uses it.
  const copyAwaitsSave = canEdit && changedRepo && isBindingSecretCopy(draft.token);
  const canConnect =
    canEdit && connectReady({ url, refusals, needsToken, copyAwaitsSave, pending: probePending });
  return { needsToken, tokenError, body, refused, saveable, copyAwaitsSave, canConnect };
}

/** SF5: what was cleaned out of a pasted address. */
function UrlNote({ note }: { note: string | null }) {
  if (!note) return null;
  return (
    <SkyText as="p" size={200} color="muted" data-testid="dbt-url-note">
      {note}
    </SkyText>
  );
}

/** The branches Connect listed, with "Use a tag or commit instead". */
function BranchSelect({
  branch,
  options,
  onText,
  onBranch,
}: {
  branch: string;
  options: Array<{ value: string; text: string }>;
  onText: () => void;
  onBranch: (branch: string) => void;
}) {
  return (
    <SkyDropdown
      label="Branch"
      testId="dbt-branch-select"
      selectedOptions={branch.trim() ? [branch.trim()] : []}
      options={options}
      listboxMaxHeight="320px"
      onOptionSelect={(values) => {
        const value = values[0];
        if (!value) return;
        if (value === USE_TEXT_REF) {
          onText();
          return;
        }
        onBranch(value);
      }}
    />
  );
}

/** The project folders Connect found, with "Type a folder". */
function FolderSelect({
  subdir,
  options,
  onText,
  onSubdir,
}: {
  subdir: string;
  options: Array<{ value: string; text: string }>;
  onText: () => void;
  onSubdir: (subdir: string) => void;
}) {
  return (
    <SkyDropdown
      label="Project folder"
      testId="dbt-folder-select"
      selectedOptions={[subdir.trim() === "" ? ROOT_FOLDER : subdir.trim()]}
      options={options}
      onOptionSelect={(values) => {
        const value = values[0];
        if (!value) return;
        if (value === USE_TEXT_FOLDER) {
          onText();
          return;
        }
        onSubdir(value === ROOT_FOLDER ? "" : value);
      }}
    />
  );
}

export function DbtSourceForm({
  binding,
  draft,
  canEdit,
  allowCopy = false,
  saver,
  onDraftChange,
}: {
  binding: InstanceBindingView;
  draft: DbtDraft;
  canEdit: boolean;
  /** B28: offer "Copy from an existing vault secret" for the token (platform admins only). */
  allowCopy?: boolean;
  saver: BindingSaveState<DbtSourceSaveBody>;
  onDraftChange: (draft: DbtDraft) => void;
}) {
  const probe = useDbtProbe();
  const [result, setResult] = useState<DbtProbeResult | null>(null);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [urlNote, setUrlNote] = useState<string | null>(null);
  const [replacing, setReplacing] = useState(false);
  const [branchAsText, setBranchAsText] = useState(false);
  const [folderAsText, setFolderAsText] = useState(false);
  // The draft as of the last commit. Connect awaits the server for up to ~30 s, and anything
  // typed meanwhile must survive: its update is built from this, not from the closure.
  const latestDraft = useRef(draft);
  useLayoutEffect(() => {
    latestDraft.current = draft;
  });

  const readOnly = !canEdit;
  const refusals = dbtSourceRefusals(draft, binding);
  const changedRepo = repoChanged(draft, binding);
  const { needsToken, tokenError, body, refused, saveable, copyAwaitsSave, canConnect } = formState(
    {
      draft,
      binding,
      canEdit,
      refusals,
      changedRepo,
      saveError: saver.error,
      probePending: probe.isPending,
    },
  );

  const set = (patch: Partial<DbtDraft>): void => onDraftChange({ ...draft, ...patch });

  /** What Connect found belongs to one address; another address starts from nothing. */
  function forgetConnect(): void {
    setResult(null);
    setConnectError(null);
    setBranchAsText(false);
    setFolderAsText(false);
  }

  /** SF5: clean a pasted `<org>@` when the field is left, and say so. */
  function normaliseUrl(): string {
    const { url: cleaned, removed } = stripDbtRepoUrlUsername(draft.repoUrl.trim());
    if (removed !== null) {
      set({ repoUrl: cleaned });
      setUrlNote(removedNote(removed, cleaned));
    }
    return cleaned;
  }

  async function connect(): Promise<void> {
    const address = normaliseUrl();
    setConnectError(null);
    const request = probeRequestOf(draft, refusals, address);
    // An answer for an address the user has since left describes nothing on screen.
    const stillThere = (): boolean =>
      addressKey(latestDraft.current.repoUrl) === addressKey(address);
    try {
      const answer = await probe.mutateAsync(request);
      if (!stillThere()) return;
      setResult(answer);
      if (!answer.ok) return;
      // Built on the draft as it is NOW.
      const latest = latestDraft.current;
      const picked = connectPicks(latest, answer);
      setBranchAsText(picked.branchAsText);
      setFolderAsText(picked.folderAsText);
      if (Object.keys(picked.patch).length > 0) onDraftChange({ ...latest, ...picked.patch });
    } catch (cause) {
      if (!stillThere()) return;
      setResult(null);
      setConnectError(errorSentence(cause, "Try again."));
    }
  }

  const { branchOptions, tags, folderOptions, showBranchList, showFolderList } = connectChoices(
    result,
    { branchAsText, folderAsText, canEdit },
  );

  const textField = (field: DbtField, value: string, hint: string, onBlur?: () => void) => (
    <SkyInput
      label={DBT_FIELD_LABELS[field]}
      value={value}
      readOnly={readOnly}
      hint={hint}
      validationState={refusals[field] ? "error" : "none"}
      validationMessage={refusals[field]}
      testId={TEST_IDS[field]}
      onChange={(event) => {
        if (field === "repoUrl") {
          setUrlNote(null);
          if (addressKey(event.target.value) !== addressKey(draft.repoUrl)) forgetConnect();
        }
        set({ [field]: event.target.value } as Partial<DbtDraft>);
      }}
      onBlur={onBlur}
    />
  );

  return (
    <div className={`space-y-4 ${FORM_MAX_WIDTH}`}>
      <DbtIntro canEdit={canEdit} />

      <SkyCard
        appearance="outline"
        testId="dbt-card"
        header={{
          title: "dbt repository",
          description:
            "Optional. The repository your dbt jobs run from, after a load lands. The dbt jobs on the Transformations screen decide which models run.",
          action: readOnly ? (
            <SkyBadge appearance="tint" color="subtle">
              Read-only
            </SkyBadge>
          ) : undefined,
        }}
      >
        <div className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-2">
          <div className="space-y-1">
            {textField(
              "repoUrl",
              draft.repoUrl,
              "https only, with no user or token in it. GitHub or Azure DevOps for Connect.",
              canEdit
                ? () => {
                    normaliseUrl();
                  }
                : undefined,
            )}
            <UrlNote note={urlNote} />
          </div>
          <TokenField
            binding={binding}
            staged={draft.token}
            readOnly={readOnly}
            error={tokenError}
            needsToken={needsToken}
            onReplace={() => setReplacing(true)}
            onClear={() => set({ token: null })}
            onDiscardStaged={() => {
              const next = { ...draft };
              delete next.token;
              onDraftChange(next);
            }}
          />
        </div>

        {canEdit ? (
          <ConnectRow
            canConnect={canConnect}
            pending={probe.isPending}
            copyAwaitsSave={copyAwaitsSave}
            onConnect={() => void connect()}
          />
        ) : null}

        <ConnectResults result={result} connectError={connectError} />

        <div className="mt-4 grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-2">
          {showBranchList ? (
            <BranchSelect
              branch={draft.branch}
              options={branchOptions}
              onText={() => setBranchAsText(true)}
              onBranch={(branch) => set({ branch })}
            />
          ) : (
            textField("branch", draft.branch, branchHint(tags))
          )}
          {showFolderList ? (
            <FolderSelect
              subdir={draft.subdir}
              options={folderOptions}
              onText={() => setFolderAsText(true)}
              onSubdir={(subdir) => set({ subdir })}
            />
          ) : (
            textField(
              "subdir",
              draft.subdir,
              "Where dbt_project.yml lives, relative to the repository root (e.g. analytics). Empty means the root.",
            )
          )}
        </div>
      </SkyCard>

      {canEdit ? (
        <DbtSaveControls
          saver={saver}
          body={body}
          saveable={saveable}
          refusals={refusals}
          refused={refused}
          changeSummary={summarise(draft, binding)}
          replacing={replacing}
          onReplacingChange={setReplacing}
          onStageToken={(token) => set({ token })}
          allowCopy={allowCopy}
          onDiscard={() => {
            setUrlNote(null);
            forgetConnect();
            onDraftChange(dbtDraftFromBinding(binding));
          }}
        />
      ) : null}
    </div>
  );
}
